import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { uniqBy } from 'lodash';
import './Waitlist.scss';

import { getWaitlist, searchWaitlist } from '../../api/waitlist';

let searchDebouncer;
const WaitlistData = props => {
  const { setWaitlist, limit, offset, automationStatus, countryCode, sort, direction } = props;
  const { curSearchValue, setSearchResults, setIsLoading } = props;
  const prevAutomationStatus = useRef(automationStatus);
  const prevCountryCode = useRef(countryCode);
  const prevSort = useRef(sort);
  const prevDirection = useRef(direction);
  const searchWasActive = useRef(false);
  const requestId = useRef(0);

  useEffect(() => {
    const filterChanged =
      automationStatus !== prevAutomationStatus.current ||
      countryCode !== prevCountryCode.current ||
      sort !== prevSort.current ||
      direction !== prevDirection.current;
    prevAutomationStatus.current = automationStatus;
    prevCountryCode.current = countryCode;
    prevSort.current = sort;
    prevDirection.current = direction;

    const id = ++requestId.current;
    setIsLoading(true);
    if (filterChanged) setWaitlist([]);

    const params = { limit, offset: filterChanged ? 0 : offset, automationStatus, sort, direction };
    if (countryCode) params.countryCode = countryCode;

    getWaitlist(params)
      .then(waitlistUsers => {
        if (id !== requestId.current) return;
        setWaitlist(prev => (filterChanged ? waitlistUsers : uniqBy([...prev, ...waitlistUsers], 'id')));
      })
      .catch(() => {
        if (id === requestId.current) window.ALERT?.error('Error fetching waitlist users');
      })
      .finally(() => {
        if (id === requestId.current) setIsLoading(false);
      });
  }, [offset, automationStatus, countryCode, sort, direction]);

  useEffect(() => {
    let cancelled = false;
    clearTimeout(searchDebouncer);

    if (!curSearchValue) {
      setSearchResults([]);
      if (searchWasActive.current) setIsLoading(false);
      searchWasActive.current = false;
      return undefined;
    }

    if (curSearchValue.length < 3) {
      if (searchWasActive.current) setIsLoading(false);
      searchWasActive.current = false;
      return undefined;
    }

    searchWasActive.current = true;
    setIsLoading(true);
    searchDebouncer = setTimeout(() => {
      searchWaitlist({ query: curSearchValue, sort, direction })
        .then(res => {
          if (!cancelled) setSearchResults(res.searchResults);
        })
        .catch(err => console.error(err))
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(searchDebouncer);
    };
  }, [curSearchValue, sort, direction]);

  return null;
};

WaitlistData.propTypes = {
  waitlist: PropTypes.array.isRequired,
  setWaitlist: PropTypes.func.isRequired,
  limit: PropTypes.number.isRequired,
  offset: PropTypes.number.isRequired,
  automationStatus: PropTypes.string.isRequired,
  countryCode: PropTypes.string,
  sort: PropTypes.string.isRequired,
  direction: PropTypes.string.isRequired,
  curSearchValue: PropTypes.string.isRequired,
  setSearchResults: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
};

export default WaitlistData;
